"use client";

import {
  CalculatorIcon,
  ChartBarIcon,
  ListIcon,
  SearchIcon,
  TrendingUpIcon,
} from "lucide-react";
import { memo, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { promptGenerators } from "@/hooks/use-quick-action";
import { cn } from "@/lib/utils";
import { AddressDisplay } from "./address-display";
import { ChainBadge } from "./chain-badge";
import { CollapsibleCard } from "./collapsible-card";
import { QuickActionsBar } from "./quick-actions-bar";
import { TransactionRow, TransactionRowSkeleton } from "./transaction-row";
import type { QuickAction, TransactionListProps } from "./types";
import { getChainInfo } from "./types";

/**
 * Default number of transactions to show initially
 */
const DEFAULT_VISIBLE_COUNT = 5;

/**
 * Main TransactionList component - displays a list of transactions
 */
const PureTransactionList = ({
  cardId,
  input,
  output,
  state,
  onQuickAction,
  isReadonly,
}: TransactionListProps) => {
  const [visibleCount, setVisibleCount] = useState(DEFAULT_VISIBLE_COUNT);
  const chain = getChainInfo(input.chainId);

  // Handle loading states
  if (state === "input-streaming" || state === "input-available") {
    return (
      <TransactionListSkeleton
        address={input.address}
        chainId={input.chainId}
      />
    );
  }

  // Handle error state
  if (output && "error" in output) {
    return (
      <TransactionListError
        address={input.address}
        chainId={input.chainId}
        error={output.error}
      />
    );
  }

  // Handle empty output
  if (!output || output.transactions.length === 0) {
    return (
      <TransactionListEmpty
        address={input.address}
        chainId={input.chainId}
        onQuickAction={onQuickAction}
        timeRange={
          output?.timeRange ?? { start: input.startTime, end: input.endTime }
        }
      />
    );
  }

  const transactions = output.transactions;
  const visibleTransactions = transactions.slice(0, visibleCount);
  const hasMore = transactions.length > visibleCount;
  const totalCount = output.numTransactionsFound ?? transactions.length;

  // Generate quick actions for the list
  const quickActions: QuickAction[] = [
    {
      label: "Summarize activity",
      icon: <ChartBarIcon className="size-3" />,
      prompt: promptGenerators.summarizeActivity(input.address, chain.name),
    },
    {
      label: "Calculate total fees",
      icon: <CalculatorIcon className="size-3" />,
      prompt: promptGenerators.calculateFees(input.address, totalCount),
    },
    {
      label: "Find patterns",
      icon: <SearchIcon className="size-3" />,
      prompt: promptGenerators.findPatterns(input.address),
    },
    {
      label: "Largest transactions",
      icon: <TrendingUpIcon className="size-3" />,
      prompt: `Show me the largest value transactions from this list for ${input.address}. What were the biggest moves?`,
    },
  ];

  // Header content (always visible when collapsed)
  const headerContent = (
    <div
      className={cn(
        "flex items-center justify-between border-b bg-gradient-to-br p-4 pr-12",
        chain.bgGradient
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex size-10 items-center justify-center rounded-full"
          style={{ backgroundColor: `${chain.color}20` }}
        >
          <ListIcon className="size-5" style={{ color: chain.color }} />
        </div>
        <div>
          <div className="flex items-center gap-2 font-medium">
            <span>{totalCount} transactions</span>
            <ChainBadge chainId={input.chainId} size="sm" />
          </div>
          <div className="flex items-center gap-1 text-muted-foreground text-xs">
            <AddressDisplay
              address={input.address}
              chainId={input.chainId}
              showCopy={false}
              showExplorerLink={false}
              size="sm"
            />
          </div>
        </div>
      </div>

      <div className="text-right text-muted-foreground text-xs">
        <div>
          {new Date(input.startTime).toLocaleDateString()} -{" "}
          {new Date(input.endTime).toLocaleDateString()}
        </div>
        {output.message && (
          <div className="mt-1 text-amber-600 dark:text-amber-400">
            Warning: {output.message}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <CollapsibleCard cardId={cardId} headerContent={headerContent}>
      {/* Transaction list */}
      <div className="divide-y bg-background">
        {visibleTransactions.map((tx) => (
          <TransactionRow
            className="rounded-none border-0"
            enableHoverActions
            key={tx.hash}
            onQuickAction={onQuickAction}
            transaction={tx}
            userAddress={input.address}
          />
        ))}
      </div>

      {/* Load more */}
      {hasMore && (
        <div className="border-t bg-muted/30 p-3 text-center">
          <button
            className="text-primary text-sm hover:underline"
            onClick={() =>
              setVisibleCount((prev) =>
                Math.min(prev + DEFAULT_VISIBLE_COUNT, transactions.length)
              )
            }
            type="button"
          >
            Show more ({transactions.length - visibleCount} remaining)
          </button>
        </div>
      )}

      {/* Quick actions */}
      <div className="border-t bg-muted/20 p-4">
        <QuickActionsBar
          actions={quickActions}
          maxVisible={4}
          onAction={onQuickAction}
          size="sm"
        />
      </div>
    </CollapsibleCard>
  );
};

export const TransactionList = memo(PureTransactionList);

/**
 * Skeleton loader for transaction list
 */
function TransactionListSkeleton({
  address,
  chainId,
}: {
  address: string;
  chainId: string;
}) {
  const chain = getChainInfo(chainId);

  return (
    <div className="overflow-hidden rounded-xl border">
      {/* Header skeleton */}
      <div
        className={cn(
          "flex items-center justify-between border-b bg-gradient-to-br p-4",
          chain.bgGradient
        )}
      >
        <div className="flex items-center gap-3">
          <Skeleton className="size-10 rounded-full" />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-32" />
              <ChainBadge chainId={chainId} size="sm" />
            </div>
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
        <Skeleton className="h-8 w-32" />
      </div>

      {/* Loading indicator */}
      <div className="flex items-center justify-center gap-2 border-b bg-background p-4">
        <div className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="text-muted-foreground text-sm">
          Scanning blocks for transactions...
        </span>
      </div>

      {/* Skeleton rows */}
      <div className="divide-y bg-background">
        {Array.from({ length: 3 }).map((_, i) => (
          <div className="p-0" key={i}>
            <TransactionRowSkeleton />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Empty state for transaction list
 */
function TransactionListEmpty({
  address,
  chainId,
  timeRange,
  onQuickAction,
}: {
  address: string;
  chainId: string;
  timeRange: { start: string; end: string };
  onQuickAction: TransactionListProps["onQuickAction"];
}) {
  const chain = getChainInfo(chainId);

  const quickActions: QuickAction[] = [
    {
      label: "Try different time range",
      icon: <SearchIcon className="size-3" />,
      prompt: `Search for transactions from ${address} on ${chain.name} in a different time period. Maybe look at the last month or year.`,
    },
    {
      label: "Check wallet state",
      icon: <ChartBarIcon className="size-3" />,
      prompt: `Show me the current state of wallet ${address} on ${chain.name}. What tokens does it hold?`,
    },
  ];

  return (
    <div className="overflow-hidden rounded-xl border">
      <div
        className={cn(
          "flex items-center justify-between border-b bg-gradient-to-br p-4",
          chain.bgGradient
        )}
      >
        <div className="flex items-center gap-3">
          <div
            className="flex size-10 items-center justify-center rounded-full"
            style={{ backgroundColor: `${chain.color}20` }}
          >
            <ListIcon className="size-5" style={{ color: chain.color }} />
          </div>
          <div>
            <div className="flex items-center gap-2 font-medium">
              <span>No transactions found</span>
              <ChainBadge chainId={chainId} size="sm" />
            </div>
            <AddressDisplay
              address={address}
              chainId={chainId}
              showCopy={false}
              showExplorerLink
              size="sm"
            />
          </div>
        </div>
      </div>

      <div className="bg-background p-6 text-center">
        <div className="mb-2 text-muted-foreground">
          No transactions found in the specified time range
        </div>
        <div className="text-muted-foreground/70 text-xs">
          {new Date(timeRange.start).toLocaleDateString()} →{" "}
          {new Date(timeRange.end).toLocaleDateString()}
        </div>
      </div>

      <div className="border-t bg-muted/20 p-4">
        <QuickActionsBar
          actions={quickActions}
          onAction={onQuickAction}
          size="sm"
        />
      </div>
    </div>
  );
}

/**
 * Error state for transaction list
 */
function TransactionListError({
  error,
  address,
  chainId,
}: {
  error: string;
  address: string;
  chainId: string;
}) {
  return (
    <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4">
      <div className="flex items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-destructive/20">
          <ListIcon className="size-4 text-destructive" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-destructive">
            Failed to retrieve transactions
          </h4>
          <p className="mt-1 text-destructive/80 text-sm">{error}</p>
          <div className="mt-2 text-destructive/70 text-xs">
            Address: {address}
          </div>
        </div>
      </div>
    </div>
  );
}
